import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router";

import {
  queryKeys,
  useProjectOverviewQuery,
  usePreviewTargetsQuery,
} from "@/api/hooks";
import {
  classifyFreezeFailure,
  classifyPatientListingFailure,
  classifySessionFailure,
  reportPreviewSessionOutcome,
  sessionFailureOutcome,
  useCreatePreviewSessionMutation,
  useFreezeCandidateMutation,
  usePreviewCandidatesQuery,
  usePreviewPatientsQuery,
  type CandidateSummary,
  type CreatePreviewSessionRequest,
  type FreezeFailure,
  type PatientListingFailure,
  type SessionFailure,
} from "@/api/preview";
import { classifySignoffFailure, useCreateSignoffMutation } from "@/api/publication";
import { useCandidateCreationGate } from "@/api/validation";
import { useValidationStore } from "@/state/validationState";
import { useApiClient } from "@/api/ApiContext";
import { useFinalQuestioningQuery } from "@/api/finalQuestioning";
import { useCapability, useSession } from "@/auth/SessionContext";
import { useRuntimeConfig } from "@/config/RuntimeConfigContext";
import { isCurrent, supersessionSentence } from "@/lib/candidateFreshness";
import { formatDateTime } from "@/lib/format";
import { lifecycleRefusalCopy } from "@/lib/lifecycle";
import { previewRunPath } from "@/lib/previewInsights";
import { Button } from "@/ui/Button";
import { Dialog, DialogContent } from "@/ui/Dialog";
import { Tooltip } from "@/ui/Tooltip";
import { useToast } from "@/ui/Toast";
import { LoadingState, StaleDataNotice } from "@/ui/states";
import { PreviewTargetControl } from "@/ui/preview/PreviewTargetControl";
import { RuntimeSafetyDetection } from "@/ui/preview/RuntimeSafetyDetection";
import {
  admissionRefusalSentence,
  refusalRecovery,
} from "@/ui/preview/finalQuestioningRefusal";
import { FinalQuestioningWarning } from "@/ui/project/FinalQuestioningWarning";
import {
  PreviewTargetNotice,
  PREVIEW_TARGET_NOT_ENABLED_COPY,
  type PreviewTargetAvailability,
} from "@/ui/preview/PreviewTargetNotice";

/**
 * Preview as a patient — the route-backed canonical modal over the Project
 * Overview (#518).
 *
 * This replaces the full-page Preview surface (#260/#261/#262/#338) with the
 * canonical authority's modal: one testing-version card, a test-patient
 * choice, and a freeze-and-launch journey, rendered in a dialog over the
 * Overview at `/projects/:projectId/preview`. The address is real — open it
 * and the modal is open; close the modal and the address is the Overview's —
 * which is what keeps a deep link, the browser's Back button, and the modal
 * agreeing about where the clinician is.
 *
 * WHAT THE CANONICAL AUTHORITY GOVERNS HERE, AND WHAT IT DOES NOT.
 *
 * Composition, copy and states follow the live canonical reference, re-read at
 * implementation time (2026-08-26). Three deliberate deviations, each because
 * a repository-ratified rule outranks the reference on what the product is
 * allowed to DO (`src/ui/README.md` § Canonical authority):
 *
 *  1. The minted preview URL is NEVER rendered. The canonical prototype prints
 *     it in the opened panel; here the link is itself the single-use credential
 *     (#261, ADR 0001), so the panel offers "Copy link" and the already-opened
 *     tab, and the bytes stay out of the DOM.
 *  2. The Details disclosure adds the preview target and its compare-and-set
 *     revision to the canonical metadata rows. #260's ratified requirement —
 *     target and revision displayed on the preview surface — survives the move
 *     into a modal; `src/invariants/projectUxAmendment.test.ts` pins it here.
 *  3. The #338 retarget control stays mounted, gated on `preview.configure`
 *     exactly as before, rendering nothing for everyone else. The canonical
 *     reference puts retargeting on the Overview, which #517 owns; deleting
 *     the only mount before #517 lands would retire a ratified capability.
 *
 * The canonical "App experience" section renders its honest empty state: the
 * Editor Service declares no app-experience listing today, and this product
 * does not fabricate choices (states are covered "as the service declares
 * them today" — the ticket's own boundary). The section, not a dead select,
 * is what a deployment that offers experiences would extend.
 *
 * STATE THE DIALOG OWNS, exactly as the full-page route owned it: the chosen
 * test patient and the launch outcome. Deliberately NOT in the URL — ADR 0001
 * forbids a candidate or patient identifier in any browser address, and
 * `src/invariants/routes.test.ts` pins the route parameters.
 */

/**
 * The canonical card labels, keyed by the states the service can declare.
 *
 * `previous` is the server's `freshness: superseded` reading: the newest
 * frozen version no longer represents the Project's current effective
 * configuration — the draft, an adoption or the base moved after the freeze.
 * It outranks `expired` on the label because it changes what a launch DOES
 * (a new freeze, never this version), while expiry is stated on its own line
 * whenever it holds.
 */
type CandidateCardState = "current" | "previous" | "expired";

const CARD_STATE_LABEL: Record<CandidateCardState, string> = {
  current: "Current",
  previous: "Previous",
  expired: "Expired",
};

/**
 * Stated when a launch found, on its own re-read, that the version it was
 * about to launch had stopped being current — another tab or another
 * clinician moved the configuration since this dialog read it.
 */
const CURRENCY_MOVED_COPY =
  "This test version no longer matches the project's current configuration, so it was not launched. Launch again to freeze a new test version of the current configuration.";

/**
 * The server's validity reading (#650), verbatim: derived against the server
 * clock at the instant the listing was served, so the judgment is pinned to
 * the same instant as the data it judges — the property this function's
 * retired `dataUpdatedAt` date-math approximated in the browser, now held
 * where the clock is. Every served row carries a reading (legacy rows get
 * the derived 14-day bound); a cached snapshot without one is not evidence
 * of expiry and fails toward "not expired" — the service still holds every
 * load gate.
 */
function isExpired(candidate: CandidateSummary): boolean {
  return candidate.validity?.state === "expired";
}

/**
 * The freeze refusals, in the sentences the retired CandidateFreezeControl
 * carried (#261, revised by #497) — the states are the service's and did not
 * move when the surface did. `satisfies` so a new kind fails the build rather
 * than rendering blank.
 */
export const FREEZE_FAILURE_COPY = {
  preview_target: PREVIEW_TARGET_NOT_ENABLED_COPY,
  // #497 dropped the "or it has no saved edits to freeze" half: a project
  // without saved edits is no longer refused — its adoption-only composition
  // is itself freezable — so naming that as a possible cause would send a
  // clinician looking for a problem the service no longer has.
  not_validated:
    "The Editor Service refused to freeze this configuration: it did not pass authoritative validation. Validate the current configuration and try again.",
  stale_draft:
    "The draft changed after it was validated, so freezing it now would capture a version nobody reviewed. Validate the current draft, then freeze again.",
  idempotency_conflict:
    "This freeze attempt was already used for a different effective configuration — the base, saved override, or adopted library content has changed since. It has been discarded, so freezing again starts a fresh attempt.",
  stale_adoptions:
    "The effective base or adopted library content changed while this configuration was being validated, so freezing now would capture a combination nobody reviewed. Validate the current configuration, then freeze again.",
  denied:
    "You are not authorized to freeze a preview candidate for this project. Ask a workspace administrator for the preview.create permission.",
  unavailable:
    "Freezing a preview candidate is not available in this environment yet. The Editor Service accepts the request but has not implemented it, so there is nothing to retry.",
  error:
    "The Editor Service did not confirm a candidate. Nothing about this draft has been assumed in either direction.",
} satisfies Record<FreezeFailure["kind"], string>;

/** The patient-listing refusals, likewise carried over verbatim (#261/#262). */
const LISTING_FAILURE_COPY = {
  upstream:
    "The test patients for this project's preview target could not be listed: the target is not reachable from this deployment right now, or it is not serving previews. Nothing here is wrong with the project — try again, or ask a workspace administrator to check the target.",
  denied:
    "You are not authorized to list test patients for this project. Ask a workspace administrator for the preview.create permission.",
  unavailable:
    "Listing test patients is not available in this environment yet. The Editor Service accepts the request but has not implemented it, so there is nothing to retry.",
  error:
    "The test patients for this project's preview target could not be listed. Nothing has been assumed about which fixtures exist.",
} satisfies Record<PatientListingFailure["kind"], string>;

/** The session refusals (#262), unchanged: the service's states, not the page's. */
const SESSION_FAILURE_COPY = {
  stale_target:
    "This project's preview target changed while this page was open, so nothing was launched: a preview created now would have run against a target nobody on this screen chose.",
  // PROVENANCE-NEUTRAL, deliberately. The Editor refuses some of these itself,
  // before the session request is sent — a target that could not be asked, or
  // a candidate this workspace records no posture for — so naming the target
  // as the refuser would be wrong for exactly the cases where the target was
  // never consulted. `admissionRefusalSentence` says who and why where it can.
  admission_refused:
    "The preview was refused because this test version's safety posture could not be cleared for the preview target, so no preview session was started.",
  preview_target: PREVIEW_TARGET_NOT_ENABLED_COPY,
  candidate_unusable:
    "The Editor Service would not preview that test version: it is not from this project, or it has expired. Re-read the available versions and choose again.",
  candidate_validator_mismatch:
    "This immutable test version was validated by a different Brain build than this deployment uses. Rebuild it here before launching.",
  patient_gone:
    "The preview target no longer offers that test patient — the fixture list this page is showing has gone stale. Look for test patients again and choose one of the current fixtures.",
  denied:
    "You are not authorized to create a preview session for this project. Ask a workspace administrator for the preview.create permission.",
  unavailable:
    "This deployment of the Editor Service does not serve preview sessions. That is a property of the deployment rather than of anything on this screen, so there is nothing here to retry.",
  upstream:
    "The preview could not be created: this deployment could not reach the project's preview target, or that target is not serving previews. Nothing has been launched, and nothing about the candidate has been assumed.",
  validator_pin_unavailable:
    "This Editor deployment cannot establish its configured Brain build, so it refused the preview before launch. Ask a workspace administrator to repair the deployment pin.",
  error:
    "The Editor Service did not confirm a preview session. Nothing has been launched.",
} satisfies Record<SessionFailure["kind"], string>;

/**
 * One recovery control per refusal, or none (`null`) where the recovery is not
 * this clinician's to perform — the retired launch control's rule, kept.
 */
const SESSION_RECOVERY_LABEL = {
  stale_target: "Reload the preview target",
  // The refusal is about the candidate's frozen posture against a live
  // target's policy. Re-reading what this deployment can observe is the one
  // act that can change the answer on this screen — neither retrying the
  // launch nor freezing again is a recovery a clinician can reason about here.
  admission_refused: "Check the target's safety detection again",
  preview_target: "Check the target again",
  candidate_unusable: "Look for test versions again",
  candidate_validator_mismatch: "Rebuild test version",
  patient_gone: "Look for test patients again",
  denied: null,
  unavailable: null,
  upstream: "Check the target again",
  validator_pin_unavailable: null,
  error: "Try again",
} satisfies Record<SessionFailure["kind"], string | null>;

/**
 * The acceptance refusals this dialog can hit. The sentences match
 * `PublicationLane`'s where the state is the same state — one copy per
 * refusal, wherever it is rendered — except `no_preview_evidence`, whose
 * recovery here is the dialog's own journey rather than a navigation.
 */
const SIGNOFF_FAILURE_COPY: Record<string, string> = {
  not_evidence_eligible:
    "This version cannot be accepted for publication. Freeze a new version once the approved safety-rule catalogue is in place.",
  no_preview_evidence:
    "Preview this version as a patient first, against this Project's current preview target, then accept it. A change of preview target also means previewing again.",
  denied: "You are not authorized to accept a version for this Project.",
  unavailable: "Accepting a version is not available in this environment yet.",
  error: "The version could not be accepted. Try again.",
};

/**
 * The identity tuple a launch carries forward — all three fields, because the
 * acceptance that may follow cites the exact tuple, never the id alone (#16).
 * Both `CandidateSummary` and the freeze response satisfy it structurally.
 */
// Spelled structurally (no `Pick`): the identifier-literal spelling would trip
// the ADR 0001 lint rule, and the contract types these as plain strings.
type LaunchedCandidate = {
  candidateId: string;
  contentHash: string;
  basePackSha: string;
};

type LaunchState =
  | { kind: "idle" }
  | { kind: "launching" }
  | {
      kind: "launched";
      candidate: LaunchedCandidate;
      previewUrl: string;
      expiresAt: string;
      /**
       * #1096's opaque run locator, carried so the launched state can offer
       * the second of the design's two independent actions — Open Preview
       * Insights (#1098). Not a credential: the URL above is the only one on
       * this response, and the locator authorizes nothing by itself.
       */
      previewRunRef: string;
    }
  | { kind: "failure"; failure: SessionFailure };

export function ProjectPreviewDialog({ projectId }: { projectId: string }) {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const apiClient = useApiClient();
  const config = useRuntimeConfig();
  const { session } = useSession();
  /**
   * Whether this account may open Preview Insights (#1098). Read here so the
   * launched state can DISABLE the action with its reason rather than hide
   * it — ADR 0001's rule for unauthorized controls — and so the run page is
   * never linked to from a session the service would refuse.
   */
  const inspect = useCapability("preview.inspect");

  const overview = useProjectOverviewQuery(projectId);
  const candidates = usePreviewCandidatesQuery(projectId);
  const patients = usePreviewPatientsQuery(projectId);
  const targets = usePreviewTargetsQuery();

  const freeze = useFreezeCandidateMutation(projectId);
  const createSession = useCreatePreviewSessionMutation(projectId);
  const signoff = useCreateSignoffMutation(projectId);
  /**
   * The #7 content gate remains one half of candidate creation: a freeze acts
   * on the validated draft version it certified, and the pin travels with the
   * request. #650's lifecycle verdict supplies the other half — authorization
   * and archive state — so this surface does not re-derive either.
   */
  const gate = useCandidateCreationGate(projectId);
  /**
   * The Project's CURRENT final-questioning posture (#853), read here so this
   * surface can tell it apart from the immutable one a candidate froze. The
   * same cache entry the header, Safety and validation warnings read, so the
   * four cannot disagree.
   */
  const finalQuestioning = useFinalQuestioningQuery(projectId);
  const beginValidationResync = useValidationStore(
    (state) => state.beginResync,
  );
  const settleValidationResync = useValidationStore(
    (state) => state.setIfCurrent,
  );

  const [chosenPatientRef, setPatientRef] = useState<string | null>(null);
  const [launch, setLaunch] = useState<LaunchState>({ kind: "idle" });
  const [freezeFailure, setFreezeFailure] = useState<FreezeFailure | null>(null);
  const [currencyMoved, setCurrencyMoved] = useState(false);
  const [signoffFailure, setSignoffFailure] = useState<string | null>(null);
  const [confirmingAcceptance, setConfirmingAcceptance] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);

  /**
   * The chosen patient, reconciled with the latest DEFINED successful listing
   * snapshot: a stale-on-mount, focus, or recovery refetch that no longer
   * offers the chosen fixture RETIRES the choice, exactly as the retired
   * `PreviewPatientSelect` did — otherwise the launch would POST a fixture
   * this dialog can no longer even display, and explain itself only through
   * the service's refusal. Cleared during render — React's documented
   * adjust-state-on-data-change form, which restarts this render with the
   * cleared state — so the judgment is pinned to the same render as the data
   * it judges AND the stored choice is actually gone. Masking it in a derived
   * value was this clause's first form, and it resurrected the choice: a
   * listing round-trip [A,B] → [B] → [A,B] re-pressed A and re-enabled
   * Launch with no new clinician click, because the hidden state was still A
   * (review finding 518-patient-selection-reconcile, cycle 2). A withdrawn
   * fixture may be reprovisioned under the same ref with different contents;
   * only a clinician can choose it again. An unreadable snapshot (`data`
   * unchanged on error) clears nothing: not knowing which fixtures exist is
   * not evidence the chosen one is gone.
   */
  const patientListing = patients.data;
  const chosenPatientWithdrawn =
    chosenPatientRef !== null &&
    patientListing !== undefined &&
    !patientListing.items.some((item) => item.ref === chosenPatientRef);
  if (chosenPatientWithdrawn) {
    setPatientRef(null);
  }
  // Also masked for the remainder of THIS pass (React discards it, but every
  // read below must already see the retired choice as gone).
  const patientRef = chosenPatientWithdrawn ? null : chosenPatientRef;

  /**
   * One freeze attempt, one idempotency key — minted at intent and retired on
   * success, the discipline the retired CandidateFreezeControl established so
   * a retry after a lost response replays rather than minting twice.
   */
  const freezeKey = useRef<string | null>(null);
  const startedAt = useRef<number | null>(null);

  /**
   * A retarget invalidates the PATIENT choice and any minted session — the
   * full-page route's rule (#338, PR #386 review), kept verbatim in modal
   * form: the revision this surface launches with is the revision it read,
   * and a revision that moves under it drops the choice scoped to the old
   * target, resets the launch outcome (the minted URL was the old target's
   * credential), and states the move instead of hiding it.
   */
  const revision = overview.data?.project.previewTargetRevision ?? null;
  const seenRevision = useRef<number | null>(null);
  const [revisionMoved, setRevisionMoved] = useState<{
    from: number;
    to: number;
  } | null>(null);
  useEffect(() => {
    if (revision === null) {
      return;
    }
    if (seenRevision.current !== null && seenRevision.current !== revision) {
      setRevisionMoved({ from: seenRevision.current, to: revision });
      setPatientRef(null);
      setLaunch({ kind: "idle" });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.previewPatients(projectId),
      });
      // #853: the runtime-detection answers are TARGET-DERIVED — each names the
      // target and revision it was computed against — so a moved revision makes
      // every cached one describe a target this Project no longer has. Without
      // this the block below would keep naming the old target beside a dialog
      // that has already moved to the new one.
      //
      // HERE rather than in `useChangePreviewTargetMutation`, and that is the
      // module's own ruling rather than convenience: the retarget writer
      // deliberately splices the summary and invalidates nothing target-scoped,
      // because "a second mechanism for that would be a defect rather than belt
      // and braces" (PR #335 review, #338). This effect is the one mechanism,
      // and it fires whoever moved the revision — this tab through the spliced
      // overview, or anyone else through a refetch of it.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.candidateRuntimeDetections(projectId),
      });
    }
    seenRevision.current = revision;
  }, [revision, projectId, queryClient]);

  /**
   * Two-phase close: the dialog closes FIRST (`open` → false), so Radix runs
   * its close lifecycle — including the focus return to the opener — and the
   * navigation to the project root follows in the effect below. Navigating
   * directly would unmount an open dialog, which skips exactly the focus
   * restore the route-backed-modal criterion requires; and because the
   * Overview renders the same component type at both addresses, its DOM (the
   * opener included) survives the navigation for that focus to land on.
   */
  const [open, setOpen] = useState(true);
  useEffect(() => {
    if (!open) {
      void navigate(`/projects/${encodeURIComponent(projectId)}`);
    }
  }, [open, navigate, projectId]);
  function close() {
    setOpen(false);
  }

  const project = overview.data?.project ?? null;
  const lifecycle = overview.data?.lifecycle ?? null;
  const freezeVerdict = lifecycle?.capabilities.freezeCandidate ?? null;

  const candidateItems = candidates.data?.items ?? [];
  /**
   * An unreadable candidate list is UNKNOWN, never empty. Rendering the
   * failure as `[]` would promise "a frozen test version will be made" and
   * offer the freeze-and-launch write — minting another immutable candidate —
   * when the truth is that the existing candidates merely could not be read.
   * With no data at all the card area states the failure and the launch is
   * held; with cached rows the card renders from them under an explicit
   * stale notice, and the service still holds the real gate.
   */
  const candidatesUnreadable =
    candidates.isError && candidates.data === undefined;
  const readAt = candidates.dataUpdatedAt;
  /** Newest first, per the listing contract. */
  const latest = candidateItems[0] ?? null;
  /**
   * The one candidate a plain launch may use: the NEWEST, and only while the
   * server says it is `current` — it still represents the Project's current
   * effective configuration — and not expired. Both readings are the
   * service's (`freshness`, `validity`), never derived here.
   *
   * This replaces `candidateItems.find((item) => !isExpired(item))`, which
   * selected any non-expired candidate as current: after a Project's BG sets
   * were removed and the changed configuration validated, that line still
   * offered `Test version 1 · Current` and launched it as if it were the
   * configuration on screen. An older candidate is never a launch target
   * either — it is superseded by the freeze after it whatever its content —
   * and an absent reading is not current (fail closed, `isCurrent`).
   */
  const launchable = (candidate: CandidateSummary): boolean =>
    isCurrent(candidate) && !isExpired(candidate);
  const eligible = latest !== null && launchable(latest) ? latest : null;
  /**
   * The canonical "Test version N" label, from the server's own revision
   * number (#650). The position math this replaces justified itself with "the
   * contract carries no version number", and the contract now carries one — a
   * list index is not this number. A row without it (an older cached snapshot)
   * renders the label without inventing an ordinal.
   */
  const versionLabel = (candidate: CandidateSummary): string =>
    candidate.revision
      ? `Test version ${candidate.revision.number}`
      : "Test version";

  const patientItems = patients.data?.items ?? [];

  const targetItems = targets.data?.items ?? [];
  const availability: PreviewTargetAvailability | null =
    project === null || targets.isPending
      ? null
      : targetItems.length === 0
        ? targets.isError
          ? "unchecked"
          : "none_provisioned"
        : targetItems.includes(project.previewTenantId)
          ? null
          : "not_available";

  /**
   * Freeze, minting the canonical toast. Returns the created candidate or
   * null after a stated failure. Failure copy renders beside the card, where
   * the action that failed lives.
   */
  async function freezeCandidate(): Promise<LaunchedCandidate | null> {
    setFreezeFailure(null);
    if (freezeKey.current === null) {
      freezeKey.current = crypto.randomUUID();
    }
    if (!gate.allowed || freezeVerdict?.state !== "permitted") {
      // Unreachable from the rendered controls — they disable on the gate —
      // and kept as the fail-closed backstop both contracts ask for.
      return null;
    }
    try {
      const created = await freeze.mutateAsync({
        idempotencyKey: freezeKey.current,
        ...gate.pin,
      });
      freezeKey.current = null;
      // No invented ordinal (#650/#651): the freeze response carries no
      // revision number and a count+1 guess could misnumber under a
      // concurrent freeze. The listing refetch renders the server's number.
      toast("New test version ready");
      return created;
    } catch (error) {
      const failure = classifyFreezeFailure(error);
      if (failure.kind === "idempotency_conflict") {
        freezeKey.current = null;
      }
      if (failure.kind === "preview_target") {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.previewTargets,
        });
      }
      // The service has just contradicted what this session believed about
      // the effective configuration: the freeze was refused because the
      // configuration is not durably validated (`not_validated`), or because
      // the draft or the adopted content moved past what this browser read
      // (`stale_draft`, `stale_adoptions`). Every input the gate said
      // "enabled" from is therefore suspect, and an enabled control held up
      // by disproven local state is exactly the unbounded retry #498
      // retires. So the gate's inputs are re-synced: the session's
      // now-disproven outcome is dropped and the durable inputs are re-read,
      // and the control lands where the SERVICE is — closed until the
      // current configuration is validated, or open again if the durable
      // badge really does certify the tuple the service now holds.
      //
      // The re-sync is a *blocking* interval, not a fire-and-forget:
      // `invalidateQueries` leaves the disproven cached data in place while
      // the refetch is in flight (`isPending` false, `isFetching` true), so
      // handing the verdict to `not_run` immediately would let the gate read
      // the old durable `passed` badge and re-enable the refused action
      // until the refetch settled (cycle-2 review finding). Instead the
      // outcome enters `resyncing` — which the gate refuses fail-closed —
      // and becomes `not_run` only once BOTH authoritative re-reads settle,
      // success or failure alike: a failed refetch surfaces as the draft
      // query's own error, which the gate also refuses. The settle write is
      // revision-guarded (`setIfCurrent`), so a validation run the clinician
      // starts during the interval owns the outcome and cannot be clobbered,
      // and a stray late response cannot resurrect the disproven belief.
      // Ported from the retired CandidateFreezeControl when #518's dialog
      // replaced it; the blocking interval is #572's hardening of it.
      if (
        failure.kind === "not_validated" ||
        failure.kind === "stale_draft" ||
        failure.kind === "stale_adoptions"
      ) {
        const resyncRevision = beginValidationResync(projectId);
        void Promise.allSettled([
          queryClient.invalidateQueries({
            queryKey: queryKeys.draft(projectId),
          }),
          queryClient.invalidateQueries({
            queryKey: queryKeys.projectOverview(projectId),
          }),
        ]).then(() => {
          settleValidationResync(projectId, resyncRevision, {
            kind: "not_run",
          });
        });
      }
      setFreezeFailure(failure);
      return null;
    }
  }

  /**
   * Whether the version this dialog would launch is STILL the current one,
   * asked of the service at the moment of the launch rather than of the
   * listing this dialog rendered from. The listing is re-read on every mount,
   * but a modal can stay open while another tab, or another clinician,
   * removes an adoption; launching from the earlier snapshot would start a
   * preview of a configuration nobody on this screen chose — the same class
   * of race the acceptance path re-reads Overview for. A failed re-read
   * withholds the launch too: not knowing is not evidence of currency.
   */
  async function stillCurrent(candidate: LaunchedCandidate): Promise<boolean> {
    const refreshed = await candidates.refetch().catch(() => null);
    if (refreshed === null || refreshed.isError || !refreshed.data) {
      return false;
    }
    const newest = refreshed.data.items[0];
    return (
      newest !== undefined &&
      newest.candidateId === candidate.candidateId &&
      newest.contentHash === candidate.contentHash &&
      newest.basePackSha === candidate.basePackSha &&
      launchable(newest)
    );
  }

  /**
   * The launch. With an eligible candidate it launches that one — after
   * confirming with the service that it is still current — without one it
   * freezes first — the canonical "Freeze & launch preview" — so what is
   * tested cannot change underneath the clinician.
   */
  async function launchPreview() {
    if (patientRef === null || project === null) {
      return;
    }
    setCurrencyMoved(false);
    let candidate: LaunchedCandidate | null;
    if (eligible !== null) {
      setLaunch({ kind: "launching" });
      if (!(await stillCurrent(eligible))) {
        // The refetch has already re-rendered the card from the service's
        // answer (Previous / Expired / unreadable); the launch control now
        // reads "Freeze & launch preview" where a freeze is the recovery.
        // Nothing is frozen on this click: the clinician pressed "Launch
        // preview" for THIS version, and minting a new one on that intent
        // would be the silent substitution the verdict exists to prevent.
        setCurrencyMoved(true);
        setLaunch({ kind: "idle" });
        return;
      }
      candidate = eligible;
    } else {
      candidate = await freezeCandidate();
    }
    if (candidate === null) {
      return;
    }
    setLaunch({ kind: "launching" });
    setCopyFailed(false);
    startedAt.current = performance.now();
    /**
     * Exactly the contract's three fields (`additionalProperties: false`), and
     * no target: the revision below is the compare-and-set token for the
     * target this dialog DISPLAYED, and where the preview runs stays the
     * project's server-held property. `src/invariants/previewEgress.test.tsx`
     * asserts this on the bytes.
     */
    const body: CreatePreviewSessionRequest = {
      candidateId: candidate.candidateId,
      patientRef,
      expectedPreviewTargetRevision: project.previewTargetRevision,
    };
    try {
      const created = await createSession.mutateAsync(body);
      const durationMs =
        startedAt.current === null
          ? null
          : Math.round(performance.now() - startedAt.current);
      reportPreviewSessionOutcome(apiClient, {
        outcome: "created",
        sessionType: "preview",
        environment: config.environment,
        durationMs,
      });
      /**
       * Opened for the clinician, never rendered: the URL is the single-use
       * credential (#261, ADR 0001). `noopener,noreferrer` severs the opened
       * tab from this one; the return value is deliberately ignored — a
       * blocked popup leaves the Copy control below as the recovery.
       */
      window.open(created.previewUrl, "_blank", "noopener,noreferrer");
      setLaunch({
        kind: "launched",
        candidate,
        previewUrl: created.previewUrl,
        expiresAt: created.expiresAt,
        previewRunRef: created.previewRunRef,
      });
    } catch (error) {
      const failure = classifySessionFailure(error);
      const durationMs =
        startedAt.current === null
          ? null
          : Math.round(performance.now() - startedAt.current);
      const reported = sessionFailureOutcome(failure);
      reportPreviewSessionOutcome(apiClient, {
        outcome: reported.outcome,
        errorCode: reported.errorCode,
        sessionType: "preview",
        environment: config.environment,
        durationMs,
      });
      setLaunch({ kind: "failure", failure });
    }
  }

  /**
   * "Link copied" is a statement about the clipboard, so it is made only
   * after the clipboard confirms it. The URL is the single-use credential and
   * is never rendered (#261, ADR 0001) — a false success here would leave the
   * clinician with a blocked popup, no link, and no remaining way to open the
   * preview. An absent Clipboard API and a rejected write both land in the
   * same durable, stated failure, and the Copy control itself is the retry.
   */
  async function copyPreviewLink(url: string) {
    setCopyFailed(false);
    try {
      if (navigator.clipboard === undefined) {
        throw new Error("clipboard_unavailable");
      }
      await navigator.clipboard.writeText(url);
      toast("Link copied");
    } catch {
      setCopyFailed(true);
    }
  }

  function recoverFromSessionFailure(failure: SessionFailure) {
    switch (failure.kind) {
      case "stale_target":
        // The overview re-read moves the revision, and the revision effect
        // above is what clears the stale choices — one code path for a move,
        // however it is discovered.
        void queryClient.invalidateQueries({
          queryKey: queryKeys.projectOverview(projectId),
        });
        return;
      case "candidate_unusable":
        void queryClient.invalidateQueries({
          queryKey: queryKeys.previewCandidates(projectId),
        });
        setLaunch({ kind: "idle" });
        return;
      case "candidate_validator_mismatch":
        // Refreshing cannot change an immutable candidate. This recovery
        // performs the action it names, then the existing candidate-query
        // invalidation in `freezeCandidate` exposes the newly minted id.
        setLaunch({ kind: "idle" });
        void freezeCandidate();
        return;
      case "patient_gone":
        void queryClient.invalidateQueries({
          queryKey: queryKeys.previewPatients(projectId),
        });
        setPatientRef(null);
        setLaunch({ kind: "idle" });
        return;
      case "preview_target":
      case "upstream":
        void queryClient.invalidateQueries({
          queryKey: queryKeys.previewTargets,
        });
        setLaunch({ kind: "idle" });
        return;
      case "admission_refused":
        // The one read that can change this answer: what the target reports
        // about its own per-message detection. Nothing about the candidate is
        // re-frozen and nothing is re-launched — the block above re-renders
        // from the service's fresh pre-flight, and the clinician decides.
        void queryClient.invalidateQueries({
          queryKey: queryKeys.candidateRuntimeDetections(projectId),
        });
        setLaunch({ kind: "idle" });
        return;
      case "error":
        setLaunch({ kind: "idle" });
        return;
      case "validator_pin_unavailable":
        return;
      default:
        return;
    }
  }

  /**
   * Accepting cites the exact frozen identity tuple of the candidate that was
   * just previewed — read from the listing the service served, never
   * assembled. Before the write, Overview is re-read and must confirm both the
   * exact current tuple and a permitted lifecycle verdict. That closes the
   * cached-authority gap for revocation and archive changes; the signoff route
   * remains the final race-safe gate.
   */
  async function acceptPreview(candidate: LaunchedCandidate) {
    if (
      acceptBlockReason !== null ||
      confirmingAcceptance ||
      signoff.isPending
    ) {
      return;
    }
    setSignoffFailure(null);
    setConfirmingAcceptance(true);
    const confirmation = await overview.refetch().catch(() => null);
    if (confirmation === null || confirmation.isError || !confirmation.data) {
      setConfirmingAcceptance(false);
      setSignoffFailure(
        "Whether this version can be accepted could not be confirmed. Reload the project and try again.",
      );
      return;
    }

    const current = confirmation.data.draft.latestCandidate;
    const currentLifecycle = confirmation.data.lifecycle;
    if (
      current == null ||
      current.candidateId !== candidate.candidateId ||
      current.contentHash !== candidate.contentHash ||
      current.basePackSha !== candidate.basePackSha ||
      currentLifecycle.candidate == null ||
      currentLifecycle.candidate.identity.candidateId !== candidate.candidateId ||
      currentLifecycle.candidate.identity.contentHash !== candidate.contentHash ||
      currentLifecycle.candidate.identity.basePackSha !== candidate.basePackSha
    ) {
      setConfirmingAcceptance(false);
      setSignoffFailure(
        "This is no longer the project's current test version. Review and preview the current version before accepting it.",
      );
      return;
    }

    const refreshedVerdict = currentLifecycle.capabilities.acceptCandidate;
    if (refreshedVerdict.state === "refused") {
      setConfirmingAcceptance(false);
      setSignoffFailure(lifecycleRefusalCopy(refreshedVerdict.reason));
      return;
    }

    try {
      await signoff.mutateAsync({
        candidate: {
          candidateId: candidate.candidateId,
          contentHash: candidate.contentHash,
          basePackSha: candidate.basePackSha,
        },
      });
      toast("Approved");
    } catch (error) {
      const failure = classifySignoffFailure(error);
      setSignoffFailure(
        SIGNOFF_FAILURE_COPY[failure.kind] ?? SIGNOFF_FAILURE_COPY["error"] ?? "",
      );
    } finally {
      setConfirmingAcceptance(false);
    }
  }

  const status = overview.data?.publicationStatus ?? null;
  const acceptedCurrent =
    status !== null &&
    latest !== null &&
    status.candidateAcceptance === "accepted" &&
    status.acceptedCandidateId === latest.candidateId;

  /**
   * The acceptance verdict only describes the lifecycle's operative candidate.
   * A just-frozen candidate can reach the launched panel before the invalidated
   * Overview refetch settles, so an older verdict must never be applied to it.
   * `no_qualifying_preview` remains live for the same reason as the Publication
   * lane: the qualifying load event arrives after the preview opens, and the
   * write is the fresh authority during that short interval. Every other
   * refusal for the exact launched tuple is stable enough to withhold the POST
   * and render the server's reason before the click.
   */
  const acceptVerdictForLaunch =
    launch.kind === "launched" &&
    lifecycle?.candidate !== null &&
    lifecycle?.candidate !== undefined &&
    lifecycle.candidate.identity.candidateId === launch.candidate.candidateId &&
    lifecycle.candidate.identity.contentHash === launch.candidate.contentHash &&
    lifecycle.candidate.identity.basePackSha === launch.candidate.basePackSha
      ? lifecycle.capabilities.acceptCandidate
      : null;
  const acceptBlockReason =
    launch.kind === "launched" && lifecycle === null
      ? "Whether this version can be accepted could not be read. Reload the project and try again."
      : acceptVerdictForLaunch?.state === "refused" &&
          acceptVerdictForLaunch.reason !== "lifecycle.no_qualifying_preview"
        ? lifecycleRefusalCopy(acceptVerdictForLaunch.reason)
        : null;

  const targetId = project?.previewTenantId ?? null;
  const description =
    "See how the patient app looks with this setup. Nothing is sent to a real patient." +
    (targetId ? ` Test patients come from ${targetId}.` : "");

  const cardState: CandidateCardState | null =
    latest === null
      ? null
      : !isCurrent(latest)
        ? "previous"
        : isExpired(latest)
          ? "expired"
          : "current";
  /**
   * The server's sentence for a superseded newest version — what moved, and
   * that a launch freezes a new one. Rendered inside the card, and it doubles
   * as the launch control's explanation (`preview-freeze-explanation`) in
   * exactly the way the no-candidate promise does, so a keyboard user hears
   * why the control reads "Freeze & launch" before pressing it.
   */
  const supersession =
    latest === null ? null : supersessionSentence(latest.freshness);

  const launching = launch.kind === "launching" || freeze.isPending;
  /**
   * "Freeze & launch" needs the gate; a plain launch of an already-eligible
   * candidate does not — the candidate is immutable and the gate governs only
   * what a NEW freeze would capture.
   */
  const freezeBlocked =
    eligible === null &&
    (!gate.allowed || freezeVerdict?.state !== "permitted");
  const launchReady =
    patientRef !== null &&
    project !== null &&
    !launching &&
    !freezeBlocked &&
    !candidatesUnreadable;
  const launchReason = candidatesUnreadable
    ? "The test versions could not be listed, so nothing can be launched until they load."
    : freezeBlocked
      ? freezeVerdict?.state === "refused"
        ? lifecycleRefusalCopy(freezeVerdict.reason)
        : gate.allowed
          ? "Whether a new test version can be frozen could not be read. Reload the project and try again."
          : gate.detail
      : patientRef === null
        ? "Select a test patient to preview as."
        : null;
  /**
   * Whether the pre-freeze explanation paragraph is in the DOM — the exact
   * condition of its render branch below, so the launch control's
   * `aria-describedby` never names an id that is not rendered. While it is,
   * the explanation joins the control's accessible description in the blocked
   * state as well as the enabled one (#498: what a freeze makes stays
   * keyboard-reachable and associated with the action, exactly as worth
   * knowing while a validation is still owed as after it) — the refusal
   * first, because it is the sentence that changes.
   */
  const explanationVisible =
    !overview.isPending &&
    !candidates.isPending &&
    project !== null &&
    !candidatesUnreadable &&
    (latest === null || cardState === "previous");
  const launchDescribedBy =
    [
      launchReady ? null : "preview-launch-reason",
      explanationVisible ? "preview-freeze-explanation" : null,
    ]
      .filter((id) => id !== null)
      .join(" ") || undefined;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !launching) {
          close();
        }
      }}
    >
      <DialogContent
        title="Real patient chat"
        description={description}
        busy={launching}
        footer={
          <>
            <Button variant="outline" onClick={close} disabled={launching}>
              Close
            </Button>
            <Button
              onClick={() => void launchPreview()}
              disabled={!launchReady}
              aria-describedby={launchDescribedBy}
            >
              {eligible ? "Launch preview" : "Freeze & launch preview"}
            </Button>
          </>
        }
      >
        {overview.isPending || candidates.isPending ? (
          <LoadingState label="Loading preview" rows={3} />
        ) : project === null ? (
          <p role="alert" className="text-sm text-foreground">
            This project could not be loaded, so nothing can be previewed from
            here. Close this and open the project again.
          </p>
        ) : (
          <div className="space-y-4">
            {revisionMoved === null ? null : (
              <p
                role="status"
                aria-live="polite"
                className="rounded-md border border-border bg-muted/40 p-3 text-sm text-foreground"
              >
                {`This project's preview target moved from revision ${revisionMoved.from} to revision ${revisionMoved.to} while this page was open. The test patient chosen against the old target was cleared; choose one again from the fixtures the target now offers.`}
              </p>
            )}
            {currencyMoved ? (
              <p
                role="status"
                aria-live="polite"
                className="rounded-md border border-border bg-muted/40 p-3 text-sm text-foreground"
              >
                {CURRENCY_MOVED_COPY}
              </p>
            ) : null}

            {availability ? (
              <PreviewTargetNotice
                availability={availability}
                onRefresh={() => void targets.refetch()}
              />
            ) : null}

            {/*
              THE PREVIEW-ENTRY copy of §8's persistent safety warning (#853).
              About the DRAFT — what a freeze from here would carry — which is
              why it stands above the testing-version card rather than inside
              it. What the existing candidate actually froze, and what that
              means against this target, is the read-only runtime-detection
              block below.
            */}
            <FinalQuestioningWarning projectId={projectId} surface="preview" />

            {/*
              The testing-version card, the promise a launch makes one, or —
              when the listing could not be read — the failure itself. Never
              the empty-list promise over an unreadable list: that would turn
              a read failure into a write path (see `candidatesUnreadable`).
            */}
            {candidatesUnreadable ? (
              <div role="alert" className="space-y-2 text-sm text-foreground">
                <p>
                  The test versions for this project could not be listed, so
                  this dialog cannot tell whether a frozen version already
                  exists. Nothing has been assumed in either direction, and
                  nothing can be launched until the list can be read.
                </p>
                <Button
                  variant="outline"
                  onClick={() => void candidates.refetch()}
                >
                  Try again
                </Button>
              </div>
            ) : candidates.isError ? (
              <StaleDataNotice
                updatedAt={readAt}
                onRetry={() => void candidates.refetch()}
              />
            ) : null}
            {candidatesUnreadable ? null : latest === null ? (
              <p
                id="preview-freeze-explanation"
                className="rounded-md border border-border px-3 py-2 text-xs text-muted-foreground"
              >
                A frozen test version will be made when you launch, so what you
                test can&apos;t change underneath you.
              </p>
            ) : (
              <div className="rounded-lg border border-border bg-muted/20 px-3 py-2.5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium">
                      {`${versionLabel(latest)} · ${cardState ? CARD_STATE_LABEL[cardState] : ""}`}
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {`Made ${formatDateTime(latest.createdAt)}${targetId ? ` · for ${targetId}` : ""}`}
                    </p>
                    {latest.expiresAt ? (
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {`Can be tested until ${formatDateTime(latest.expiresAt)}`}
                      </p>
                    ) : null}
                    {/*
                      The server's own reading of a superseded version — what
                      moved, and that a launch freezes a new one. The version
                      stays on the card as history: never renamed, renewed or
                      removed. When no reading was served at all the version
                      is still not launchable (`isCurrent` fails closed); the
                      sentence then says only that much, without inventing a
                      reason the server did not give.
                    */}
                    {cardState === "previous" ? (
                      <p
                        id="preview-freeze-explanation"
                        className="mt-1 text-xs text-warning-foreground"
                      >
                        {supersession ??
                          "Whether this test version still matches the current configuration was not stated by the Editor Service, so it will not be launched as the current one. Launching freezes a new test version of the current configuration."}
                      </p>
                    ) : null}
                    {isExpired(latest) ? (
                      <p className="mt-1 text-xs text-warning-foreground">
                        This test version has expired, so it can no longer be
                        opened.
                      </p>
                    ) : null}
                  </div>
                  {cardState === "expired" ? (
                    <Button
                      variant="outline"
                      onClick={() => void freezeCandidate()}
                      disabled={
                        freeze.isPending ||
                        !gate.allowed ||
                        freezeVerdict?.state !== "permitted"
                      }
                      aria-describedby={
                        gate.allowed && freezeVerdict?.state === "permitted"
                          ? undefined
                          : "preview-launch-reason"
                      }
                    >
                      {freeze.isPending ? "Rebuilding…" : "Rebuild test version"}
                    </Button>
                  ) : null}
                </div>
                <button
                  type="button"
                  onClick={() => setDetailsOpen((open) => !open)}
                  aria-expanded={detailsOpen}
                  className="mt-2 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                >
                  Details
                </button>
                {detailsOpen ? (
                  <dl className="mt-1.5 grid grid-cols-[130px_minmax(0,1fr)] gap-x-3 gap-y-1 text-xs">
                    {[
                      ["Candidate id", latest.candidateId],
                      ["Content hash", latest.contentHash],
                      ["Base pack", latest.basePackSha],
                      [
                        "Validation",
                        `${latest.evidence.validationProfileVersion} · ${latest.evidence.guardCatalogRevision}`,
                      ],
                      [
                        // #260's ratified display, carried into the modal: the
                        // target and the compare-and-set revision the launch
                        // will send are the ones this dialog read.
                        "Target",
                        `Preview target: ${project.previewTenantId} · revision ${project.previewTargetRevision}`,
                      ],
                    ].map(([label, value]) => (
                      <div key={label} className="contents">
                        <dt className="text-muted-foreground">{label}</dt>
                        <dd className="break-all font-mono">{value}</dd>
                      </div>
                    ))}
                  </dl>
                ) : null}
              </div>
            )}

            {freezeFailure === null ? null : (
              <p role="alert" className="text-sm text-foreground">
                {FREEZE_FAILURE_COPY[freezeFailure.kind]}
              </p>
            )}

            {/*
              RUNTIME SAFETY DETECTION, separately and read-only (#853).

              Beneath the testing-version card because it is about that exact
              frozen candidate: the §8 admission decision for its immutable
              posture against the target this dialog would launch at. The
              decision is the Editor Service's — this surface renders it and
              cannot compute, soften or override it — and it is displayed for
              the newest candidate whether or not that candidate is launchable,
              because a superseded or expired one is still what a clinician is
              looking at while they decide to rebuild.

              Absent when there is no candidate at all: there is nothing frozen
              to have a posture, and the draft's own posture is already stated
              by the warning above.
            */}
            {latest === null ? null : (
              <RuntimeSafetyDetection
                projectId={projectId}
                candidateId={latest.candidateId}
                candidateLabel={versionLabel(latest)}
                draftEffective={finalQuestioning.data?.effective ?? null}
                // The listing already says whether this candidate carries a
                // posture at all. One frozen before postures were recorded has
                // none, and the runtime-detection route refuses exactly that
                // shape — so the absence is stated rather than fetched.
                postureRecorded={latest.finalQuestioning != null}
              />
            )}

            {/*
              The canonical App experience section, in the state the service
              declares: no app-experience listing exists in the Editor Service
              contract today, and this product renders the truthful absence
              rather than a select with nothing in it (ADR 0001's no-dead-
              controls rule; a deployment that offers experiences extends this
              section, not a placeholder).
            */}
            <div className="space-y-1">
              <p className="text-xs font-medium">App experience</p>
              <p className="text-xs text-muted-foreground">
                This deployment offers no app experiences to choose from. The
                preview opens the target&apos;s patient app as it is.
              </p>
            </div>

            <div className="space-y-1">
              <p className="text-xs font-medium">Test patient</p>
              <p className="text-xs text-muted-foreground">
                {targetId
                  ? `Only designated test patients from ${targetId} are listed. Real patients are never shown.`
                  : "Only designated test patients are listed. Real patients are never shown."}
              </p>
            </div>
            {patients.isPending ? (
              <LoadingState label="Loading test patients" rows={2} />
            ) : patients.isError && patientItems.length === 0 ? (
              <div role="alert" className="space-y-2 text-sm text-foreground">
                <p>
                  {
                    LISTING_FAILURE_COPY[
                      classifyPatientListingFailure(patients.error).kind
                    ]
                  }
                </p>
                {["denied", "unavailable"].includes(
                  classifyPatientListingFailure(patients.error).kind,
                ) ? null : (
                  <Button
                    variant="outline"
                    onClick={() => void patients.refetch()}
                  >
                    Try again
                  </Button>
                )}
              </div>
            ) : patientItems.length === 0 ? (
              <p className="rounded-md border border-border px-3 py-3 text-xs text-muted-foreground">
                No test patients are available for this preview target.
              </p>
            ) : (
              <div className="max-h-56 divide-y divide-border overflow-y-auto rounded-md border border-border">
                {patientItems.map((patient) => (
                  <button
                    key={patient.ref}
                    type="button"
                    aria-pressed={patientRef === patient.ref}
                    onClick={() => {
                      setPatientRef(patient.ref);
                      if (revisionMoved !== null) {
                        setRevisionMoved(null);
                      }
                    }}
                    className={`flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-muted ${
                      patientRef === patient.ref ? "bg-muted" : ""
                    }`}
                  >
                    <span className="text-sm font-medium">{patient.label}</span>
                  </button>
                ))}
              </div>
            )}

            {launchReason === null || launching ? null : (
              <p
                id="preview-launch-reason"
                className="text-xs text-muted-foreground"
              >
                {launchReason}
              </p>
            )}

            {launch.kind === "launching" ? (
              <p
                role="status"
                aria-live="polite"
                className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-xs text-muted-foreground"
              >
                Opening the patient app…
              </p>
            ) : null}

            {launch.kind === "launched" ? (
              <div
                role="status"
                aria-live="polite"
                className="rounded-md border border-success-foreground/40 bg-success/30 px-3 py-2.5"
              >
                <p className="text-xs font-medium">Preview opened</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  It opened in a new tab. The link is not shown here: it is
                  itself the credential, can be opened once, and expires{" "}
                  {formatDateTime(launch.expiresAt)}. Copy it only to hand it to
                  the tab that will open it.
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={() => void copyPreviewLink(launch.previewUrl)}
                  >
                    Copy link
                  </Button>
                  {/*
                    The design's second independent action (§10.1): the native
                    run page, addressed by the opaque locator alone. A LINK
                    rather than a button because it is a navigation — the
                    Editor tab moves to the run page while the patient app
                    stays open in the tab `window.open` gave it — and a link
                    keeps its middle-click and its address. The href carries
                    the Project id and the locator and nothing else;
                    `src/invariants/previewEgress.test.tsx` asserts that.
                  */}
                  {inspect.allowed ? (
                    <Button variant="outline" asChild>
                      <Link
                        to={previewRunPath(projectId, launch.previewRunRef)}
                      >
                        Open Preview Insights
                      </Link>
                    </Button>
                  ) : (
                    <Tooltip content={inspect.reason ?? "Open Preview Insights"}>
                      <Button
                        variant="outline"
                        disabled
                        aria-describedby="preview-insights-reason"
                      >
                        Open Preview Insights
                      </Button>
                    </Tooltip>
                  )}
                  {acceptedCurrent ? null : (
                    <Button
                      onClick={() => void acceptPreview(launch.candidate)}
                      disabled={
                        signoff.isPending ||
                        confirmingAcceptance ||
                        acceptBlockReason !== null
                      }
                      aria-describedby={
                        acceptBlockReason === null
                          ? undefined
                          : "preview-accept-reason"
                      }
                    >
                      {signoff.isPending || confirmingAcceptance
                        ? "Approving…"
                        : "Approve this version"}
                    </Button>
                  )}
                </div>
                {acceptBlockReason === null ? null : (
                  <p
                    id="preview-accept-reason"
                    className="mt-2 text-xs text-muted-foreground"
                  >
                    {acceptBlockReason}
                  </p>
                )}
                {inspect.allowed ? null : (
                  <p
                    id="preview-insights-reason"
                    className="mt-2 text-xs text-muted-foreground"
                  >
                    {inspect.reason} Preview Insights stays closed to this
                    account; the patient preview above is unaffected.
                  </p>
                )}
                {copyFailed ? (
                  <p role="alert" className="mt-2 text-xs text-foreground">
                    The link was not copied — this browser blocked clipboard
                    access or refused the write. Nothing about the preview was
                    affected; use Copy link to try again.
                  </p>
                ) : null}
                {acceptedCurrent && status?.acceptedAt ? (
                  <p className="mt-2 text-xs text-muted-foreground">
                    {`Approved by ${
                      status.acceptedBy === session.user.subject
                        ? "you"
                        : "another clinician"
                    } on ${formatDateTime(status.acceptedAt)}. Next: send it for review. Approving does not publish it.`}
                  </p>
                ) : null}
              </div>
            ) : null}

            {signoffFailure === null ||
            signoffFailure === acceptBlockReason ? null : (
              <p role="alert" className="text-sm text-foreground">
                {signoffFailure}
              </p>
            )}

            {launch.kind === "failure" ? (
              <div role="alert" className="space-y-2 text-sm text-foreground">
                <p>
                  {SESSION_FAILURE_COPY[launch.failure.kind]}
                  {launch.failure.kind === "stale_target"
                    ? launch.failure.currentRevision === undefined
                      ? " The Editor Service reports a different revision now."
                      : ` The Editor Service now reports revision ${launch.failure.currentRevision}.`
                    : ""}
                  {launch.failure.kind === "admission_refused"
                    ? ` ${admissionRefusalSentence(launch.failure)}`
                    : ""}
                </p>
                {launch.failure.kind === "admission_refused" &&
                launch.failure.admissionReason ===
                  "final_questioning_incompatible" &&
                refusalRecovery(launch.failure.refusalReason) !== null ? (
                  <p className="text-muted-foreground">
                    {refusalRecovery(launch.failure.refusalReason)}
                  </p>
                ) : null}
                {SESSION_RECOVERY_LABEL[launch.failure.kind] === null ? null : (
                  <Button
                    variant="outline"
                    onClick={() => recoverFromSessionFailure(launch.failure)}
                  >
                    {SESSION_RECOVERY_LABEL[launch.failure.kind]}
                  </Button>
                )}
              </div>
            ) : null}

            {/*
              The #338 retarget control, gated on `preview.configure` inside
              the control itself — absent, not disabled, for everyone else.
              See the header for why it stays mounted here until #517 gives it
              its canonical Overview home.
            */}
            {lifecycle === null ? (
              <p role="alert" className="text-sm text-muted-foreground">
                Whether the preview target can be changed could not be read.
                Reload the project and try again.
              </p>
            ) : (
              <PreviewTargetControl
                projectId={projectId}
                currentTarget={project.previewTenantId}
                revision={project.previewTargetRevision}
                verdict={lifecycle.capabilities.changePreviewTarget}
              />
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
